import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AuthLayout } from '@/components/AuthLayout';
import { logActivity } from '@/lib/activityLogger';

// Helper to validate redirect URLs for security
const getValidRedirect = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:') return null;
    if (!url.hostname.endsWith('.bluelux.com')) return null;
    return url.toString();
  } catch {
    return null;
  }
};

export default function Verify() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const { email, phone, requiresBoth, type, redirectUrl } = location.state || {};
  const validRedirectUrl = getValidRedirect(redirectUrl);

  useEffect(() => {
    if (!phone && !email) {
      navigate('/auth/login');
    }

    // Check if coming from email magic link
    const checkEmailVerification = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Failed to get session:', error);
          toast.error('Failed to verify email. Please try again.');
          return;
        }
        
        // If user has a session and came from email magic link
        if (session?.user && type === 'email') {
          toast.success('Email verified! Welcome back.');
          
          // Redirect using cookie-based auth (no token handoff needed)
          const target = validRedirectUrl || '/';
          if (target.startsWith('http')) {
            window.location.href = target;
          } else {
            navigate(target, { replace: true });
          }
          return;
        }
        
        // For signup flow with email confirmation
        if (session?.user?.email_confirmed_at) {
          setEmailVerified(true);
          if (!requiresBoth) {
            toast.success('Email verified!');
            navigate('/');
          }
        }
      } catch (err) {
        console.error('Error checking email verification:', err);
        toast.error('Verification error. Please try again.');
      }
    };

    checkEmailVerification();
  }, [email, phone, navigate, requiresBoth, type]);

  const handleVerifyPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!phoneOtp || phoneOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.verifyOtp({
        phone: phone,
        token: phoneOtp,
        type: 'sms',
      });

      if (error) throw error;

      setPhoneVerified(true);
      
      // Update profile to mark phone as verified
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('profiles')
          .update({ phone_verified: true })
          .eq('id', user.id);
      }

      toast.success('Phone verified successfully!');
      
      // Redirect using cookie-based auth (no token handoff needed)
      if (!requiresBoth || (requiresBoth && emailVerified)) {
        const target = validRedirectUrl || '/';
        if (target.startsWith('http')) {
          window.location.href = target;
        } else {
          navigate(target);
        }
      }
    } catch (error: any) {
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!emailOtp || emailOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.verifyOtp({
        email: email,
        token: emailOtp,
        type: 'email',
      });

      if (error) throw error;

      toast.success('Email verified! Welcome back.');
      
      // Redirect using cookie-based auth (no token handoff needed)
      const target = validRedirectUrl || '/';
      if (target.startsWith('http')) {
        window.location.href = target;
      } else {
        navigate(target, { replace: true });
      }
    } catch (error: any) {
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone: phone,
      });

      if (error) throw error;
      toast.success('New code sent!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailOtp = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          shouldCreateUser: false,
        }
      });

      if (error) throw error;
      toast.success('New code sent to your email!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Verify Your Account</h1>
            <p className="text-muted-foreground">
              {requiresBoth 
                ? 'Please verify both your email and phone number'
                : type === 'phone' 
                ? 'Enter the code sent to your phone'
                : type === 'email'
                ? 'Enter the code from your email or click the magic link'
                : 'Check your email to verify your account'}
            </p>
          </div>

          <div className="space-y-6">
            {requiresBoth && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Email Verification</span>
                  {emailVerified ? (
                    <span className="text-xs bg-green-500/20 text-green-600 px-2 py-1 rounded">
                      ✓ Verified
                    </span>
                  ) : (
                    <span className="text-xs bg-yellow-500/20 text-yellow-600 px-2 py-1 rounded">
                      Pending
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {emailVerified 
                    ? 'Your email has been verified'
                    : 'Check your email and click the verification link'}
                </p>
              </div>
            )}

            {type === 'email' && !requiresBoth && (
              <form onSubmit={handleVerifyEmail} className="space-y-4">
                <div>
                  <Label htmlFor="emailOtp">Email Verification Code</Label>
                  <Input
                    id="emailOtp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={emailOtp}
                    onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    required
                    className="text-center text-2xl tracking-widest"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Enter the 6-digit code sent to {email}, or click the magic link in your email
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify Email'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleResendEmailOtp}
                  disabled={loading}
                >
                  Resend Code
                </Button>
              </form>
            )}

            {type === 'phone' && (
              <form onSubmit={handleVerifyPhone} className="space-y-4">
              <div>
                <Label htmlFor="phoneOtp">Phone Verification Code</Label>
                <Input
                  id="phoneOtp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={phoneOtp}
                  onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  required
                  className="text-center text-2xl tracking-widest"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the 6-digit code sent to {phone}
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={loading || phoneVerified}>
                {loading ? 'Verifying...' : phoneVerified ? '✓ Phone Verified' : 'Verify Phone'}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={handleResendOtp}
                disabled={loading}
              >
                Resend Code
              </Button>
            </form>
            )}
          </div>

          {requiresBoth && emailVerified && phoneVerified && (
            <div className="mt-6">
              <Button
                className="w-full"
                onClick={() => navigate('/')}
              >
                Continue to Team Portal
              </Button>
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
